import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { ButterCup, Neutrals, SemanticError, SemanticSuccess } from '../../color-palette/colors';
import { SAVE_BUTTON_MIN_WIDTH, shellVar } from '../Screen/screenLayout';

import type { DockActionsLayout, DockPlacement, StickyActionBarTone } from './types';

interface DockTone {
  readonly text: string;
  readonly icon: string;
  readonly background?: string;
  readonly border: string;
}

const DOCK_TONE: Readonly<Record<StickyActionBarTone, DockTone>> = {
  neutral: { text: Neutrals[700], icon: Neutrals[700], border: Neutrals[100] },
  success: { text: SemanticSuccess[900], icon: SemanticSuccess[900], border: Neutrals[100] },
  warning: { text: ButterCup[900], icon: ButterCup[900], background: ButterCup[50], border: ButterCup[100] },
  error: { text: SemanticError[900], icon: SemanticError[600], background: SemanticError[50], border: SemanticError[100] },
};

const ACTIONS_GAP = { xs: 2, md: 3 } as const;
const TERTIARY_CLEARANCE = 4;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const negate = (value: string): string => `calc(-1 * ${value})`;

const pageXsSx = (theme: Theme): SystemStyleObject<Theme> => ({
  insetBlockEnd: negate(shellVar('blockEnd')),
  marginBlockStart: 'auto',
  marginInlineStart: negate(shellVar('inlineStart')),
  marginInlineEnd: negate(shellVar('inlineEnd')),
  marginBlockEnd: negate(shellVar('blockEnd')),
  paddingInlineStart: shellVar('inlineStart'),
  paddingInlineEnd: shellVar('inlineEnd'),
  boxShadow: theme.customShadows.cardElevated,
});

const pageMdSx = (theme: Theme): SystemStyleObject<Theme> => ({
  marginBlockStart: theme.spacing(6),
  marginInline: 0,
  marginBlockEnd: 0,
  paddingInline: 0,
  boxShadow: 'none',
});

export const dockRootSx =
  (tone: StickyActionBarTone, placement: DockPlacement) =>
  (theme: Theme): SystemStyleObject<Theme> => {
    const palette = DOCK_TONE[tone];
    const page = placement === 'page';
    return {
      position: 'sticky',
      insetBlockEnd: 0,
      zIndex: theme.zIndex.mobileStepper,
      isolation: 'isolate',
      display: 'flex',
      flexDirection: 'column',
      gap: ACTIONS_GAP.xs,
      paddingBlockStart: 3,
      paddingBlockEnd: `max(env(safe-area-inset-bottom), ${theme.spacing(3)})`,
      backgroundColor: palette.background ?? theme.palette.background.paper,
      borderBlockStart: `1px solid ${palette.border}`,
      ...(page ? pageXsSx(theme) : {}),
      [theme.breakpoints.up('md')]: {
        ...(page ? pageMdSx(theme) : {}),
        flexDirection: 'row',
        alignItems: 'center',
        gap: ACTIONS_GAP.md,
        paddingBlockEnd: theme.spacing(3),
      },
      '& .MuiButton-root': {
        minHeight: theme.controlHeight.medium,
        whiteSpace: 'normal',
        textWrap: 'balance',
      },
      '& .MuiButton-sizeSmall:not(.MuiButton-text)': {
        paddingBlock: theme.spacing(2),
        paddingInline: theme.spacing(4),
      },
      [REDUCED_MOTION]: { transition: 'none !important', animation: 'none !important' },
    };
  };

const riseKeyframes = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: none; opacity: 1; }
`;

export const dockRiseSx = (theme: Theme): SystemStyleObject<Theme> => ({
  overflowX: 'visible',
  overflowY: 'clip',
  '& > *': {
    animation: `${riseKeyframes} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut} both`,
  },
  [REDUCED_MOTION]: { '& > *': { animation: 'none' } },
});

export const dockMessageSx =
  (tone: StickyActionBarTone) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    minInlineSize: 0,
    overflowWrap: 'anywhere',
    color: DOCK_TONE[tone].text,
    '& .MuiSvgIcon-root': { color: DOCK_TONE[tone].icon },
    '&:empty': { position: 'absolute', inlineSize: 0, blockSize: 0, overflow: 'hidden' },
    [theme.breakpoints.up('md')]: { flex: '1 1 0' },
  });

export const dockActionsSx =
  (actionsLayout: DockActionsLayout) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    display: 'flex',
    flexDirection: actionsLayout === 'stack' ? 'column' : 'row',
    alignItems: actionsLayout === 'stack' ? 'stretch' : 'center',
    gap: ACTIONS_GAP.xs,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: ACTIONS_GAP.md,
      flex: '0 1 auto',
      marginInlineStart: 'auto',
    },
  });

const stretchChildSx: SystemStyleObject<Theme> = { '& > *': { flex: '1 1 auto', inlineSize: '100%' } };
const autoChildSx: SystemStyleObject<Theme> = { '& > *': { flex: '0 0 auto', inlineSize: 'auto' } };

export const dockSlotSx = {
  start: (theme: Theme): SystemStyleObject<Theme> => ({
    display: 'flex',
    [theme.breakpoints.up('md')]: { marginInlineEnd: 'auto' },
  }),
  legacy:
    (actionsLayout: DockActionsLayout) =>
    (theme: Theme): SystemStyleObject<Theme> => ({
      display: 'flex',
      flexDirection: 'column',
      alignSelf: actionsLayout === 'stack' ? 'stretch' : 'auto',
      [theme.breakpoints.up('md')]: { alignSelf: 'auto' },
    }),
  tertiary:
    (actionsLayout: DockActionsLayout) =>
    (theme: Theme): SystemStyleObject<Theme> => ({
      display: 'flex',
      alignSelf: actionsLayout === 'stack' ? 'center' : 'auto',
      marginInlineEnd: actionsLayout === 'row' ? theme.spacing(TERTIARY_CLEARANCE - ACTIONS_GAP.xs) : 0,
      [theme.breakpoints.up('md')]: {
        alignSelf: 'auto',
        marginInlineEnd: theme.spacing(TERTIARY_CLEARANCE - ACTIONS_GAP.md),
      },
    }),
  secondary:
    (actionsLayout: DockActionsLayout) =>
    (theme: Theme): SystemStyleObject<Theme> => ({
      display: 'flex',
      ...(actionsLayout === 'stack' ? stretchChildSx : {}),
      [theme.breakpoints.up('md')]: autoChildSx,
    }),
  primary:
    (actionsLayout: DockActionsLayout) =>
    (theme: Theme): SystemStyleObject<Theme> => ({
      display: 'flex',
      ...stretchChildSx,
      ...(actionsLayout === 'row' ? { flex: '1 1 auto', minInlineSize: 0 } : {}),
      [theme.breakpoints.up('md')]: {
        flex: '0 0 auto',
        '& > *': { flex: '0 0 auto', inlineSize: 'auto', minInlineSize: `min(100%, ${SAVE_BUTTON_MIN_WIDTH}px)` },
      },
    }),
} as const;
